import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import trTR from 'antd/locale/tr_TR';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import CompleteRegistration from './pages/CompleteRegistration';
import Dashboard from './pages/Dashboard';
import BrandProfile from './pages/BrandProfile';
import BrandsManagement from './pages/BrandsManagement';
import Products from './pages/Products';
import SizeCharts from './pages/SizeCharts';
import BrandIntegration from './pages/BrandIntegration';
import Settings from './pages/Settings';
import FeatureUsage from './pages/FeatureUsage';
import FeaturePricing from './pages/FeaturePricing';
import WaitingConfig from './pages/WaitingConfig';
import UnifiedAnalytics from './pages/UnifiedAnalytics';
import AppointmentsAdmin from './pages/AppointmentsAdmin';
import VideoReviewPage from './pages/VideoReviewPage';
import VideoManagement from './pages/VideoManagement';
import FashMotionPage from './pages/FashMotionPage';
import ProductToImage from './pages/ProductToImage';
import Credits from './pages/Credits';
import AdminCreditManagement from './pages/AdminCreditManagement';
import './App.css';

const App: React.FC = () => {
  return (
    <ConfigProvider locale={trTR}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/complete-registration" element={<CompleteRegistration />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Navigate to="/dashboard" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/brand-profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BrandProfile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/brands"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout>
                    <BrandsManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Products />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/size-charts"
              element={
                <ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}>
                  <Layout>
                    <SizeCharts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-to-image"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProductToImage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fashion-motion"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FashMotionPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/brand-integration"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BrandIntegration />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feature-usage"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FeatureUsage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/credits"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Credits />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feature-pricing"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout>
                    <FeaturePricing />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/credits"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout>
                    <AdminCreditManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UnifiedAnalytics />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/waiting-config"
              element={
                <ProtectedRoute>
                  <Layout>
                    <WaitingConfig />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/appointments"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout>
                    <AppointmentsAdmin />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/video-review/:productCode"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VideoReviewPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/video-management/:productCode"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VideoManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;
